import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdVisibility, MdVisibilityOff, MdRefresh, MdContentCopy, MdCheck } from "react-icons/md";
import { FiAlertTriangle, FiLink } from "react-icons/fi";
import { toast } from "react-toastify";
import styles from "./OwnerTeamPanel.module.scss";
import UserCard from "components/Team/userCard/UserCard";
import Axios from "_services/caller.service";


function OwnerTeamPanel({
  ownerId,
  ownerEmail,
  userAuth,
  isLoading,
  users,
  allCabines,
  displayRoleByUser,
  selectDefaultValue,
  numberCabsAuth,
  maj,
  setMaj,
  majRole,
  setMajRole,
  setUpdateRole,
  setAllCabPermissions,
  setCabPermission,
  refreshTeam,
}) {
  console.log(" OwnerTeamPanel re-rendered - users.length:", users?.length);
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const [linking, setLinking] = useState(false);

  // Reset password state
  const [resetTarget, setResetTarget] = useState(null); // { id, label } or null
  const [resetPassword, setResetPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);
  const [resetting, setResetting] = useState(false);

  const [form, setForm] = useState({
    email: "",
    firstname: "",
    lastname: "",
    password: "",
    password2: "",
    group: "",
  });

  // Groupes de permissions
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    setLoadingGroups(true);
    Axios.get("/permissions-levels-groups")
      .then((res) => setGroups(res.data))
      .catch((err) => {
        console.error("Erreur chargement des groupes:", err);
        setGroups([]);
      })
      .finally(() => setLoadingGroups(false));
  }, []);

  const updateForm = (patch) => setForm((p) => ({ ...p, ...patch }));

  const resetForm = () => {
    setForm({
      email: "",
      firstname: "",
      lastname: "",
      password: "",
      password2: "",
      group: "",
    });
    setExistingUser(null);
  };

  const memoSetMaj = useCallback((fn) => setMaj(fn), [setMaj]);
  const memoSetMajRole = useCallback((fn) => setMajRole(fn), [setMajRole]);
  const memoSetUpdateRole = useCallback((val) => setUpdateRole(val), [setUpdateRole]);

  const [sortKey, setSortKey] = useState(null);
  const handleSort = useCallback((key) => setSortKey(key), []);

  const ownerEntry = useMemo(() => {
    if (!ownerId) return null;
    return {
      ID: ownerId,
      id: ownerId,
      email: ownerEmail || "",
      user_email: ownerEmail || "",
      user_login: ownerEmail || `owner-${ownerId}`,
      display_name: ownerEmail || `Owner #${ownerId}`,
      first_name: "",
      last_name: "",
      cabines: [allCabines || []],
    };
  }, [ownerId, ownerEmail, allCabines]);

  const filteredUsers = useMemo(() => {
    const q = (search || "").toLowerCase().trim();

    const teamUsers = (users || []).filter((u) => {
      if (String(u.ID ?? u.id) === String(ownerId)) return false;
      if (!q) return true;
      const dn = String(u.display_name || "").toLowerCase();
      const fn = String(u.first_name || u.firstname || "").toLowerCase();
      const ln = String(u.last_name || u.lastname || "").toLowerCase();
      const login = String(u.user_login || u.login || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const id = String(u.ID || u.id || "").toLowerCase();
      return (
        dn.includes(q) ||
        fn.includes(q) ||
        ln.includes(q) ||
        login.includes(q) ||
        email.includes(q) ||
        id.includes(q)
      );
    });

    if (!ownerEntry) return teamUsers;

    if (q) {
      const ownerEmailStr = String(ownerEntry.email || "").toLowerCase();
      const ownerLogin = String(ownerEntry.user_login || "").toLowerCase();
      const ownerDn = String(ownerEntry.display_name || "").toLowerCase();
      const ownerIdStr = String(ownerEntry.ID || "").toLowerCase();
      const ownerMatches =
        ownerEmailStr.includes(q) ||
        ownerLogin.includes(q) ||
        ownerDn.includes(q) ||
        ownerIdStr.includes(q);
      return ownerMatches ? [ownerEntry, ...teamUsers] : teamUsers;
    }

    return [ownerEntry, ...teamUsers];
  }, [users, search, ownerEntry, ownerId]);

  const sortedUsers = useMemo(() => {
    if (!sortKey || filteredUsers.length === 0) return filteredUsers;
    const ownerList = filteredUsers.filter((u) => String(u.ID ?? u.id) === String(ownerId));
    const rest = filteredUsers.filter((u) => String(u.ID ?? u.id) !== String(ownerId));
    const dir = sortKey.startsWith("asc") ? 1 : -1;
    const sorted = [...rest].sort((a, b) => {
      let valA, valB;
      if (sortKey.endsWith("Login")) {
        valA = (a.user_login || a.login || "").toLowerCase();
        valB = (b.user_login || b.login || "").toLowerCase();
      } else {
        valA = (a.last_name || a.display_name || "").toLowerCase();
        valB = (b.last_name || b.display_name || "").toLowerCase();
      }
      return valA < valB ? -dir : valA > valB ? dir : 0;
    });
    return [...ownerList, ...sorted];
  }, [filteredUsers, sortKey, ownerId]);

  const canCreate = useMemo(() => {
    return !!userAuth;
  }, [userAuth]);

  const handleCreateUserClick = async () => {
    // Validations
    if (!form.email.trim()) {
      toast.error(t("common.enter_email"));
      return;
    }

    if (!form.firstname.trim()) {
      toast.error(t("common.enter_firstname"));
      return;
    }

    if (!form.lastname.trim()) {
      toast.error(t("common.enter_lastname"));
      return;
    }

    if (!form.password || form.password.length < 6) {
      toast.error(t("common.password_min_6"));
      return;
    }

    if (form.password !== form.password2) {
      toast.error(t("common.passwords_not_match"));
      return;
    }

    setSaving(true);
    try {
      const res = await Axios.post(`/createUser/${ownerId}`, {
        email: form.email.trim(),
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        password: form.password,
        group: form.group || null,
      });

      if (res.data?.data) {
        // Mettre à jour la liste des utilisateurs avec le nouvel utilisateur
        const newUser = res.data.data;
        
        // Ajouter l'utilisateur à la liste
        // Déclencher un refresh de la liste
        setMaj((v) => !v);

        toast.success(t("common.user_created_success", { name: newUser.display_name }));
        resetForm();
        setCreateOpen(false);
      }
    } catch (err) {
      console.error("❌ Erreur création utilisateur:", err);

      // Si 409 : email déjà utilisé, proposer la liaison
      if (err.response?.status === 409 && err.response?.data?.existingUser) {
        setExistingUser(err.response.data.existingUser);
        toast.warning(t("common.account_exists_email"));
        return;
      }

      const errorMsg = err.response?.data?.error || t("common.creation_error");
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkExistingUser = async () => {
    if (!existingUser?.id) return;

    setLinking(true);
    try {
      const res = await Axios.post(`/linkUserToOwner/${ownerId}`, {
        userId: existingUser.id,
        group: form.group || null,
      });

      setMaj((v) => !v);
      toast.success(t("common.user_linked_success"));
      resetForm();
      setCreateOpen(false);
    } catch (err) {
      console.error("❌ Erreur liaison utilisateur:", err);
      const errorMsg = err.response?.data?.error || t("common.linking_error");
      toast.error(errorMsg);
    } finally {
      setLinking(false);
    }
  };

  const generateStrongPassword = () => {
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const digits = "0123456789";
    const special = "!@#$%&*?";
    const all = upper + lower + digits + special;
    const rand = (pool) => pool[crypto.getRandomValues(new Uint8Array(1))[0] % pool.length];
    // garantit au moins 1 de chaque catégorie
    const mandatory = [rand(upper), rand(lower), rand(digits), rand(special)];
    const rest = Array.from({ length: 8 }, () => rand(all));
    const chars = [...mandatory, ...rest];
    // mélange cryptographiquement
    for (let i = chars.length - 1; i > 0; i--) {
      const j = crypto.getRandomValues(new Uint8Array(1))[0] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join("");
  };

  const generatePassword = () => {
    const pass = generateStrongPassword();
    updateForm({ password: pass, password2: pass });
  };

  const generateResetPassword = () => {
    setResetPassword(generateStrongPassword());
  };

  const openResetFor = (userId, label, email) => {
    setResetTarget({ id: userId, label, email: email || "" });
    setResetPassword("");
    setShowResetPassword(false);
    setResetCopied(false);
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetPassword("");
    setShowResetPassword(false);
    setResetCopied(false);
  };

  const handleResetPassword = async () => {
    if (!resetTarget?.id) return;
    if (!resetPassword || resetPassword.length < 6) {
      toast.error(t("common.password_min_6"));
      return;
    }

    setResetting(true);
    try {
      await Axios.post(`/admin-reset-password/${resetTarget.id}`, {
        newPassword: resetPassword,
      });
      toast.success(t("password.reset_for_user", { user: resetTarget.label }));
      closeReset();
    } catch (err) {
      console.error("Erreur reset password:", err);
      const errorMsg = err.response?.data?.error || t("common.reset_error");
      toast.error(errorMsg);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      {canCreate && (
        <div className={styles.createSection}>
          <button
            className={styles.createBtn}
            onClick={() => setCreateOpen(!createOpen)}
            disabled={saving}
          >
            + {t("common.create_user")}
          </button>

          {createOpen && (
            <div className={styles.form}>
              <div className={styles.row}>
                <div className={styles.fieldWrap}>
                  <input
                    type="email"
                    placeholder={t("common.email")}
                    value={form.email}
                    onChange={(e) => updateForm({ email: e.target.value })}
                  />
                  {form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
                    <span className={styles.fieldError}>{t("common.format_email_invalid")}</span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder={t("common.firstname")}
                  value={form.firstname}
                  onChange={(e) => updateForm({ firstname: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t("common.lastname")}
                  value={form.lastname}
                  onChange={(e) => updateForm({ lastname: e.target.value })}
                />
              </div>

              <div className={styles.row}>
                <select
                  value={form.group}
                  onChange={(e) => updateForm({ group: e.target.value })}
                >
                  <option value="">{t("common.select_group")}</option>
                  {loadingGroups ? (
                    <option disabled>{t("common.loading")}</option>
                  ) : groups.length > 0 ? (
                    groups.map((grp) => (
                      <option key={grp.id} value={grp.id}>
                        {grp.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>{t("validation.no_group_available")}</option>
                  )}
                </select>
              </div>

              <div className={styles.row}>
                <div className={styles.passwordRow}>
                  <div className={styles.passwordInput}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("common.password")}
                      value={form.password}
                      onChange={(e) => updateForm({ password: e.target.value })}
                    />
                    <button onClick={() => setShowPassword(!showPassword)} type="button" className="has-tooltip" aria-label={showPassword ? t("common.hide") : t("common.show")}>
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  <button onClick={generatePassword} type="button" className={`${styles.createBtn} has-tooltip`} aria-label={t("common.generate_password")}>
                    <MdRefresh />
                  </button>
                </div>

                <div className={styles.fieldWrap}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("common.password_confirm")}
                    value={form.password2}
                    onChange={(e) => updateForm({ password2: e.target.value })}
                  />
                  {form.password2 && form.password !== form.password2 && (
                    <span className={styles.fieldError}>{t("common.passwords_not_match")}</span>
                  )}
                </div>
              </div>

              <div className={styles.row}>
                <button className={styles.submit} onClick={handleCreateUserClick} disabled={saving || linking} type="button">
                  {saving ? t("common.creating") : t("common.create")}
                </button>
                <button className={styles.cancel} onClick={() => { setCreateOpen(false); setExistingUser(null); }} type="button">
                  {t("common.cancel")}
                </button>
              </div>

              {existingUser && (
                <div className={styles.existingUserBanner}>
                  <FiAlertTriangle className={styles.bannerIcon} />
                  <div className={styles.bannerContent}>
                    <strong>{t("common.account_exists_email")}</strong>
                    <span>ID: {existingUser.id}</span>
                  </div>
                  <button
                    type="button"
                    className={styles.btnLink}
                    onClick={handleLinkExistingUser}
                    disabled={linking}
                  >
                    <FiLink /> {linking ? t("common.linking") : t("common.link_user")}
                  </button>
                </div>
              )}

              <div className={styles.row}>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(form.password);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={styles.copyBtn}
                >
                  <MdContentCopy /> {copied ? t("common.copied") : t("common.copy_password")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder={t("common.search_user")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {resetTarget && (
        <div className={styles.resetOverlay} onClick={closeReset}>
          <div className={styles.resetPasswordSection} onClick={(e) => e.stopPropagation()}>
            <div className={styles.resetHeader}>
              <strong>{t("common.reset_password_label", { label: resetTarget.label })}</strong>
              {resetTarget.email && <span className={styles.resetEmail}>{resetTarget.email}</span>}
              <button type="button" onClick={closeReset} className={styles.resetClose}>✕</button>
            </div>
            <div className={styles.row}>
              <div className={styles.passwordRow}>
                <div className={styles.passwordInput}>
                  <input
                    type={showResetPassword ? "text" : "password"}
                    placeholder={t("common.new_password")}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                  />
                  <button onClick={() => setShowResetPassword(!showResetPassword)} type="button" className="has-tooltip" aria-label={showResetPassword ? t("common.hide") : t("common.show")}>
                    {showResetPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </button>
                </div>
                <button onClick={generateResetPassword} type="button" className={`${styles.createBtn} has-tooltip`} aria-label={t("common.generate_password")}>
                  <MdRefresh />
                </button>
              </div>
            </div>
            <div className={styles.resetActions}>
              <button type="button" className={styles.cancel} onClick={closeReset}>
                {t("common.cancel")}
              </button>
              {resetPassword && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(resetPassword);
                    setResetCopied(true);
                    setTimeout(() => setResetCopied(false), 2000);
                  }}
                  className={styles.copyBtn}
                >
                  <MdContentCopy /> {resetCopied ? t("common.copied") : t("common.copy")}
                </button>
              )}
              <button
                type="button"
                className={styles.submit}
                onClick={handleResetPassword}
                disabled={resetting || !resetPassword}
              >
                {resetting ? t("common.resetting") : t("common.validate")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && (!users || users.length === 0) ? (
        <div className={styles.empty}>{t("common.loading")}</div>
      ) : (
        <div className={styles.userCardWrap}>
          {isLoading && (
            <div className={styles.reloading}>{t("common.updating")}</div>
          )}
          <UserCard
            inviteUser={sortedUsers}
            setSorted={handleSort}
            displayRoleByUser={displayRoleByUser}
            selectDefaultValue={selectDefaultValue}
            setUpdateRole={memoSetUpdateRole}
            userAuth={userAuth}
            setMaj={memoSetMaj}
            maj={maj}
            majRole={majRole}
            setMajRole={memoSetMajRole}
            numberCabsAuth={numberCabsAuth}
            ownerIdOverride={ownerId}
            onResetPassword={(userId, label, email) => openResetFor(userId, label, email)}
            hideEmailConfirmation
          />
        </div>
      )}
    </div>
  );
}

export default OwnerTeamPanel;